/**
 * RAPIDCONTACT API — Wrapper dei REST Liferay allineato a RapidContactsResourceImpl.java.
 * I 5 metodi CRUD standard vengono da `createCrudApi`.
 *
 * Contratto backend (rest-openapi.yaml):
 *   - GET    /rapid-contacts/{groupId}   (+ page, pageSize, sort, search)  -> Page<RapidContact>
 *   - POST   /rapid-contacts/{groupId}  RapidContactInput                   -> RapidContact
 *   - GET    /rapid-contact/{rapidContactId}                                -> RapidContact
 *   - PATCH  /rapid-contact/{rapidContactId} RapidContactInput (parziale)   -> RapidContact
 *   - DELETE /rapid-contact/{rapidContactId}                                -> 200
 *
 * Plurale "rapid-contacts" / singolo "rapid-contact" (pattern Liferay).
 * `rapidContactUpdateToInput` ignora `rapidContactId` (whitelist).
 */

import type { Page, ServerListParams } from '@crm/shared';
import { httpClient } from '../../api/httpClient';
import { createCrudApi } from '../../store/createCrudApi';
import {
  rapidContactFromDto,
  rapidContactCreateToInput,
  rapidContactUpdateToInput,
  type RapidContactDto,
} from './rapidContactMappers';
import type {
  RapidContact,
  RapidContactCreatePayload,
  RapidContactUpdatePayload,
} from './rapidContactTypes';
import type { ListTypeDefinition, ListTypeEntry } from '../listType/listTypeTypes';

/**
 * ERC della picklist dello stato di follow-up (FILO-67), una dotazione di company del core.
 *
 * SYNC CROSS-LAYER: `ListTypeConstants.RAPID_CONTACT_FOLLOWUP_STATUS` in crm-core-api e il file
 * `rapid-contact-followup-status.json` di crm-core-service. Un ERC cambiato da una parte sola dà
 * una tendina vuota, senza errori.
 */
export const FOLLOWUP_STATUS_ERC = 'CRM_RAPID_CONTACT_FOLLOWUP_STATUS';

/**
 * I livelli di interesse, fissi (FILO-67).
 *
 * SYNC CROSS-LAYER: il nome delle costanti di `InterestLevel` in crm-util, che sono anche le chiavi
 * delle regole salvate in CompanySetting. Un nome cambiato da una parte sola fa tornare quel livello
 * ai predefiniti, senza errori.
 */
export const INTEREST_LEVELS = ['ZERO', 'ONE', 'TWO', 'THREE', 'FOUR', 'FIVE'] as const;
export type InterestLevel = (typeof INTEREST_LEVELS)[number];

/**
 * La regola del task di follow-up di un livello di interesse (FILO-67): se all'importazione nasce il
 * task, con che priorità e tipo (i valori interi del backend, gli stessi del task) ed entro quanti
 * giorni lavorativi dalla data di inizio dei richiami (0 = il primo giorno lavorativo).
 */
export interface RapidContactTaskRule {
  level: InterestLevel;
  task: boolean;
  priority: number;
  days: number;
  type: number;
}

/** Le regole della company: sempre sei, le predefinite se non le ha personalizzate. */
export interface RapidContactTaskRules {
  rules: RapidContactTaskRule[];
  /** Vero se la company le ha personalizzate: serve a mostrare «Ripristina predefinite». */
  custom: boolean;
}

interface CompanySettingTaskRulesDto {
  rapidContactTaskRules?: RapidContactTaskRule[];
  rapidContactTaskRulesCustom?: boolean;
}

const toTaskRules = (dto: CompanySettingTaskRulesDto): RapidContactTaskRules => ({
  rules: dto.rapidContactTaskRules ?? [],
  custom: dto.rapidContactTaskRulesCustom ?? false,
});

/**
 * Le regole dei task di follow-up, nelle impostazioni della company:
 *   - GET    /company/settings                           -> CompanySetting
 *   - PATCH  /company/settings/rapid-contact-task-rules  -> CompanySetting
 *   - DELETE /company/settings/rapid-contact-task-rules  -> CompanySetting (predefinite)
 */
export const rapidContactTaskRulesApi = {
  get: (): Promise<RapidContactTaskRules> =>
    httpClient.get<CompanySettingTaskRulesDto>('/company/settings').then((r) => toTaskRules(r.data)),

  save: (rules: RapidContactTaskRule[]): Promise<RapidContactTaskRules> =>
    httpClient
      .patch<CompanySettingTaskRulesDto>('/company/settings/rapid-contact-task-rules', { rapidContactTaskRules: rules })
      .then((r) => toTaskRules(r.data)),

  reset: (): Promise<RapidContactTaskRules> =>
    httpClient
      .delete<CompanySettingTaskRulesDto>('/company/settings/rapid-contact-task-rules')
      .then((r) => toTaskRules(r.data)),
};

/**
 * Filtro lista extra (TS-100): `imported` seleziona i contatti ancora da importare
 * (false/omesso, default) oppure quelli già importati (true, vista admin read-only).
 * Passato come query param: `createCrudApi.getAll` inoltra `params` come query string.
 */
export interface RapidContactListParams extends ServerListParams {
  imported?: boolean;
}

/** Un contatto che l'import non ha promosso a Person: resta da importare (TS-137). */
export interface RapidContactImportFailure {
  rapidContactId: number;
  name: string;
  /** Il motivo, già tradotto dal server. */
  message: string;
}

/** Esito di POST /rapid-contacts/{groupId}/import. */
export interface RapidContactImportResult {
  imported: number;
  /** I task di follow-up nati con l'import, secondo le regole dei livelli di interesse (FILO-67). */
  tasks: number;
  failed: RapidContactImportFailure[];
}

const base = createCrudApi<
  RapidContact,
  RapidContactDto,
  RapidContactCreatePayload,
  RapidContactUpdatePayload,
  RapidContactListParams
>({
  collection: '/rapid-contacts',
  single: '/rapid-contact',
  fromDto: rapidContactFromDto,
  createToInput: rapidContactCreateToInput,
  updateId: (p) => p.rapidContactId,
  updateToInput: (p) => rapidContactUpdateToInput(p),
});

export const rapidContactApi = {
  ...base,

  /**
   * Import massivo (TS-100): importa TUTTI i contatti fiera non ancora importati del sito verso
   * i contatti di sistema (Person). Nessuna selezione. TS-137: ogni contatto ha la sua
   * transazione, quindi l'esito porta gli importati e, a parte, i falliti col motivo.
   */
  /**
   * @param callStartDate il giorno di inizio dei richiami (yyyy-MM-dd). Va come mezzogiorno UTC:
   *        il backend ne tiene solo il giorno nel fuso della company, e da mezzogiorno nessun fuso
   *        europeo cambia data.
   */
  importAll: (groupId: number, callStartDate: string): Promise<RapidContactImportResult> =>
    httpClient
      .post<RapidContactImportResult>(`/rapid-contacts/${groupId}/import`, undefined, {
        params: { callStartDate: `${callStartDate}T12:00:00Z` },
      })
      .then((r) => ({ imported: r.data.imported, tasks: r.data.tasks ?? 0, failed: r.data.failed ?? [] })),

  /**
   * Le voci dello stato di follow-up, nella lingua dell'app e in ordine di creazione: all'inizio
   * quello del file, poi quelle aggiunte dal cliente in fondo. Tutte, anche le disattivate: la
   * maschera le tiene solo se già assegnate al contatto. Nessuna voce se la picklist manca.
   */
  followupStatuses: (): Promise<ListTypeEntry[]> =>
    httpClient
      .get<Page<ListTypeDefinition>>('/list-type-definitions')
      .then((r) => (r.data.items.find((d) => d.externalReferenceCode === FOLLOWUP_STATUS_ERC)?.listTypeEntries ?? [])
        .slice()
        .sort((a, b) => a.listTypeEntryId - b.listTypeEntryId)),

  /** Contatti fiera importati e collegati a un Person (per il pannello "Fiere" della scheda). */
  getByPerson: (personId: number): Promise<RapidContact[]> =>
    httpClient
      .get<Page<RapidContactDto>>(`/rapid-contacts/by-person/${personId}`)
      .then((r) => r.data.items.map(rapidContactFromDto)),
};
